import React, {useCallback} from "react";
import {observer} from "mobx-react";
import styled from "@emotion/styled";
import {Modal} from "@mui/material";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {useBeforeUnload, useBlocker} from "react-router-dom";
import {type ILeavePicksPageModalController} from "views/components/modals/modal_leave_picks_page/modal_leave_picks_page.controller";
import {PrimaryButton, PrimaryButtonInvert, PureButton} from "views/components/style";
import CloseIcon from "assets/img/icons/modal/close.svg?react";
import ErrorIcon from "assets/img/icons/modal/error.svg?react";
import {media} from "assets/css/media";

const ModalContent = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	outline: none;
	max-width: 600px;
	width: 100%;
	background: #fff;
	padding: 56px 16px 16px;
	border-radius: 4px;
	text-align: center;
	color: #151515;

	@media screen and (max-width: 640px) {
		max-width: unset;
		left: 24px;
		right: 24px;
		width: auto;
		transform: translateY(-50%);
	}

	button {
		max-width: 300px;
	}
`;

const CloseBlock = styled(PureButton)`
	width: 100%;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	position: absolute;
	top: 8px;
	right: 8px;
	padding: 8px;
`;

const Icon = styled.div`
	margin-bottom: 8px;

	@media (min-width: ${media.mobile}) {
		margin-bottom: 12px;

		svg {
			width: 70px;
			height: 70px;
		}
	}
`;

const Title = styled.h1`
	font-size: 20px;
	font-weight: 700;
	line-height: 130%;
	margin-bottom: 8px;
	color: #103365;

	@media (min-width: ${media.mobile}) {
		font-size: 28px;
		margin-bottom: 12px;
	}
`;

const Desc = styled.p`
	font-size: 16px;
	font-weight: 400;
	margin-bottom: 16px;

	@media (min-width: ${media.mobile}) {
		margin-bottom: 20px;
		font-size: 20px;
	}
`;

const Buttons = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: 12px;
	max-width: 300px;
	width: 100%;

	@media (min-width: ${media.mobile}) {
		flex-direction: row;
		gap: 24px;
		max-width: none;
	}
`;

export const LeavePicksPageModal: React.FC = observer(() => {
	const {i18n, isBlocking} = useViewController<ILeavePicksPageModalController>(
		Bindings.LeavePicksPageModal
	);
	useBeforeUnload(
		useCallback(
			(event) => {
				if (isBlocking) {
					event.returnValue = i18n.t("modal.leave_picks.desc");
					event.preventDefault();
					event.stopPropagation();
				}
			},
			[isBlocking, i18n]
		),
		{
			capture: isBlocking,
		}
	);

	const {state, proceed, reset} = useBlocker(isBlocking);

	if (state !== "blocked") {
		return null;
	}

	return (
		<Modal open>
			<ModalContent>
				<CloseBlock onClick={reset}>
					<CloseIcon />
				</CloseBlock>

				<Icon>
					<ErrorIcon />
				</Icon>

				<Title>{i18n.t("modal.leave_picks.title")}</Title>
				<Desc>{i18n.t("modal.leave_picks.desc")}</Desc>

				<Buttons>
					<PrimaryButtonInvert onClick={reset}>
						{i18n.t("modal.leave_picks.cancel")}
					</PrimaryButtonInvert>
					<PrimaryButton onClick={proceed}>
						{i18n.t("modal.leave_picks.confirm")}
					</PrimaryButton>
				</Buttons>
			</ModalContent>
		</Modal>
	);
});
